import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from '@phosphor-icons/react';

import {
  Button,
  Divider,
  Drawer,
  Empty,
  LoadingBlock,
  Pill,
} from '../../../design-system/components/desktop';
import { Cta, Sheet, SheetBody, SheetFooter, SheetHeader } from '../../../design-system/components/mobile';
import { useViewport } from '../../../design-system/hooks/useViewport';
import {
  CartLine,
  CartLines,
  SummaryNote,
  SummaryTotal,
} from '../../../design-system/patterns/cart';
import { routes } from '../../../routes/routes';
import { distinguishingFinish } from '../../../utils/finish';

/**
 * The cart drawer, the view. Pure: every input is a prop, every output a
 * callback.
 *
 * WHAT BRANCHES ON `useViewport()` AND WHY
 * ----------------------------------------
 * The surface itself. At and above 1024 this is the library `Drawer`, the
 * 460px right slide-over; below it, it is the mobile `Sheet`. That is a row of
 * the mobile-to-desktop map, and the two are not the same DOM with different
 * padding: they have different scrims, different animations and different
 * focus behaviour. Exactly one mounts.
 *
 * Everything inside is the shared `CartLine` from `patterns/cart`, the same
 * component the cart page and the checkout summary use, so a change to a cart
 * line happens once.
 *
 * WHAT WENT AWAY
 * --------------
 * - A hand-rolled slide-over with its own backdrop component, no focus trap, no
 *   Escape handler and no scroll lock. Both library surfaces bring all three.
 * - A second `CartItem` implementation, the third in the app.
 * - A `console.log('items -====', items)` shipped in the render path.
 * - The literal string "Loading your cart..." standing in for a loading state.
 * - `item.year`, which was `new Date().getFullYear()`, so every card in the
 *   drawer claimed to be from the current year regardless of its set.
 *
 * NO PROMO CODE INPUT and NO "SAVE FOR LATER": neither feature exists in this
 * app. NO SHIPPING FIGURE either, the drawer shows a subtotal and says the
 * rate is quoted at checkout.
 */
export function CartOverlayView({
  open,
  onClose,
  items = [],
  subtotal,
  loading = false,
  onQuantityChange,
  onRemove,
  onViewCart,
  /* Whether Sleevr is shipping at all, from `useFulfilmentMode` in the
     container. In in-person-only mode there is no courier rate and no
     delivery address to confirm, so the note below described a checkout step
     that does not exist. Defaults to the honest silence: say nothing about
     shipping unless told shipping is on. */
  shipsNationwide = false,
}) {
  const { isDesktop } = useViewport();

  const body = loading ? (
    <LoadingBlock>Refreshing your cart</LoadingBlock>
  ) : items.length === 0 ? (
    <Empty
      /* `inline`, not `size="sm"`. `Empty` suppresses artwork at `sm` because
         that size is for a floating layer — a dropdown, a popover — where a
         full illustration would make the panel taller than the content it
         replaced. The cart overlay is not that: it is a full-height drawer on
         desktop and a bottom sheet on a phone, both with room. `inline` is the
         modifier for exactly this ("this state lives inside a panel"), and it
         steps the illustration down to `xs` rather than dropping it. */
      inline
      icon={ShoppingCart}
      illustration="cart-is-empty"
      title="Your cart is empty"
      text="Nothing sleeved up yet. Browse singles and sealed product from verified South African sellers."
      actions={
        <Button as={Link} to={routes.MARKETPLACE} onClick={onClose}>
          Browse marketplace
        </Button>
      }
    />
  ) : (
    <CartLines>
      {items.map((item) => (
        <CartLine
          key={item.id}
          compact
          image={item.image}
          imageAlt={item.name}
          set={item.set}
          name={item.name}
          href={item.href}
          /* Same rule as the cart page: both facts in the one slot, finish
             only when it distinguishes. This line is `compact` on top of
             that, so the space is tighter still. */
          condition={
            <>
              {item.condition ? <Pill fit>{item.condition}</Pill> : null}
              {distinguishingFinish(item.finish) ? (
                <Pill fit>{distinguishingFinish(item.finish)}</Pill>
              ) : null}
            </>
          }
          unitPrice={item.unitPrice}
          quantity={item.quantity}
          max={item.max}
          lineTotal={item.lineTotal}
          onIncrease={() => onQuantityChange(item.sellerId, item.id, item.quantity + 1)}
          onDecrease={() => onQuantityChange(item.sellerId, item.id, item.quantity - 1)}
          onRemove={() => onRemove(item.sellerId, item.id)}
        />
      ))}
    </CartLines>
  );

  const totals =
    loading || items.length === 0 ? null : (
      <>
        <Divider />
        {/* No shipping figure here. The rate is quoted by the server at
            checkout once an address is confirmed, so any number on this
            surface would be invented. Open product question 1.

            That was said in a `SummaryRow` labelled "Shipping" whose value was
            the sentence "Calculated at checkout" — a label/value pair whose
            value is not a value, in the one column of this block that holds
            money. In a 390px sheet the label and the sentence each wrapped and
            the pair read as a broken row. The subtotal is the last amount, and
            the caveat follows it as prose. */}
        <SummaryTotal label="Subtotal" currency="" value={subtotal} />
        <SummaryNote>
          {shipsNationwide
            ? 'Shipping is not included. Your courier rate is quoted at checkout, once you have confirmed a delivery address.'
            : 'You collect from the seller in person and pay them when you meet.'}
        </SummaryNote>
      </>
    );

  if (isDesktop) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title="Your cart"
        kicker={items.length > 0 ? `${items.length} ${items.length === 1 ? 'item' : 'items'}` : undefined}
        footer={
          items.length > 0 ? (
            <Button block size="lg" onClick={onViewCart}>
              View cart
            </Button>
          ) : null
        }
      >
        {body}
        {totals}
      </Drawer>
    );
  }

  /* No `if (!open) return null` above this. The Sheet holds itself in the DOM
     for the 200ms slide-down and then removes itself; an early return here
     tears the node out on the closing tick and the cart vanishes instead of
     leaving. `open` is the only close signal it needs. */
  return (
    <Sheet open={Boolean(open)} onClose={onClose} aria-label="Your cart">
      <SheetHeader title="Your cart" subtitle={items.length > 0 ? `${items.length} ${items.length === 1 ? 'item' : 'items'}` : undefined} />
      <SheetBody>
        {body}
        {totals}
      </SheetBody>
      {items.length > 0 ? (
        <SheetFooter stacked>
          <Cta block onClick={onViewCart}>
            View cart
          </Cta>
        </SheetFooter>
      ) : null}
    </Sheet>
  );
}

export default CartOverlayView;
